import { useAppDispatch, useAppSelector } from '~/store/hooks'
import {
  GET_FACILITY_QUERY,
  ALL_FACILITIES_QUERY,
  GET_FACILITY_APP_BAR_DATA_QUERY,
  GET_USER_PERMISSIONS_QUERY,
  ALL_USER_FAVORITES_QUERY,
} from 'client/queries'
import {
  DELETE_FACILITY_MUTATION,
  DELETE_VERSION_MUTATION,
  UPDATE_FLOOR_MUTATION,
  DELETE_FLOOR_MUTATION,
} from 'client/mutations'
import { showAlert } from 'store/alert'
import routes from 'config/routes'
import { removeStorageItem, removeStorageItems } from 'components/Modals/ExportPDFLayoutModal/Wizard/constants'
import { useNavigate, useParams, useLocation, resolvePath } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { ComponentProps, Fragment, useState } from 'react'
import { currentUserIsCustomer } from '~/lib/currentUserIs'
import { FACILITY_PAGE_LIMIT } from '~/client/decorators/withInitialData'
import { MenuSection, MenuTrigger } from 'react-aria-components'
import { Button, ButtonProps } from '~/ui/Button'
import { motion } from 'framer-motion'
import { IconVariant } from '~/config/icons'
import { PopoverProps } from '~/ui/Popover'
import { Menu, MenuItem } from '~/ui/Menu'
import { Icon } from '~/ui/Icon'
import { Text } from 'react-aria-components'
import { Separator } from '~/ui/Separator'

type MenuDropDownButtonProps = Omit<ButtonProps, 'children'> & {
  label: string
  icon: IconVariant
  children: PopoverProps['children']
}

const MenuDropDownButton = ({ label, icon, children }: MenuDropDownButtonProps) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <MenuTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button variant="secondary" className="flex items-center rounded-none gap-2 h-full">
        <Icon size="14" name={icon} color="black" />
        <span className="hidden min-[1200px]:block text-xs max-w-[10ch] whitespace-nowrap overflow-hidden text-ellipsis">
          {label}
        </span>
        <motion.div initial={{ rotate: 0 }} animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }}>
          <Icon size="14" name="arrowDown" color="black" />
        </motion.div>
      </Button>
      <Menu variant="square" popoverProps={{ offset: 0, placement: 'bottom left', transition: { duration: 0 } }}>
        {children}
      </Menu>
    </MenuTrigger>
  )
}

type FacilityMenuItemDetails = ComponentProps<typeof MenuItem> & {
  title: string
  subtitle?: string
  icon: IconVariant
  condition: boolean
}

const TopLeft = () => {
  const { id: facilityId, floorId, versionId } = useParams<{ id: string; floorId: string; versionId: string }>()
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const facilityQuery = useQuery(GET_FACILITY_APP_BAR_DATA_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: { id: facilityId ?? '', floorId, versionId },
    skip: !facilityId,
  })
  const userId = useAppSelector(state => state.auth.userId)
  const userPermissionsQuery = useQuery(GET_USER_PERMISSIONS_QUERY, { variables: { id: userId } })
  const isCustomer = currentUserIsCustomer(userPermissionsQuery.data?.User)
  const permissions = new Set(userPermissionsQuery.data?.User?.permissions?.map(p => p?.name))

  const dispatch = useAppDispatch()

  const refetchQueries = [
    { query: GET_FACILITY_QUERY, variables: { id: facilityId, floorId: null, versionId: null } },
    { query: ALL_FACILITIES_QUERY },
  ]
  const [deleteVersion] = useMutation(DELETE_VERSION_MUTATION, { refetchQueries })
  const [deleteFacility] = useMutation(DELETE_FACILITY_MUTATION)
  const [deleteFloor] = useMutation(DELETE_FLOOR_MUTATION, { refetchQueries })
  const [updateFloor] = useMutation(UPDATE_FLOOR_MUTATION, { refetchQueries })

  const facility = facilityQuery.data?.facility
  const isValidFacility = facility !== null
  const author = facility?.author
  const floorName = facility?.floor?.name || 'Areas'
  const versionName = facility?.floor?.version?.name || 'Versions'
  const facilityName = facility?.name ?? '---'
  const isFacilityAuthor = author?.id === userId
  const isTemplate = !!facility?.isTemplate
  const item = isTemplate ? 'template' : 'facility'
  const floors = facility?.floors ?? []
  const versions = facility?.floor?.versions ?? []

  const facilityMenuOptions: FacilityMenuItemDetails[] = [
    {
      title: 'Edit facility details',
      icon: 'edit' as const,
      onAction: () => navigate(resolvePath(`edit-facility/${facilityId}`, pathname)),
      condition: isFacilityAuthor || (isTemplate && permissions.has('Edit Template')),
    },
    {
      title: `Copy this ${item}`,
      icon: 'duplicate' as const,
      onAction: () => navigate(resolvePath(`duplicate-facility/${facilityId}`, pathname)),
      condition: isValidFacility,
    },
    {
      title: 'Delete this facility',
      icon: 'trash' as const,
      onAction: () => {
        if (confirm('Are you sure you want to delete this facility?')) {
          deleteFacility({
            variables: { id: facilityId ?? '' },
            refetchQueries: [
              { query: ALL_FACILITIES_QUERY, variables: { filter: null, limit: FACILITY_PAGE_LIMIT } },
              { query: ALL_FACILITIES_QUERY, variables: { filter: { isArchived: true }, limit: FACILITY_PAGE_LIMIT } },
              { query: ALL_USER_FAVORITES_QUERY, variables: { limit: FACILITY_PAGE_LIMIT } },
            ],
          }).then(res => {
            removeStorageItems({ facility: res.data?.deleteFacility?.id })
            dispatch(showAlert({ type: 'success', text: 'Facility deleted!' }))
            navigate('/facilities')
          })
        }
      },
      condition: isFacilityAuthor && !isTemplate && permissions.has('Delete Facility'),
    },
    {
      title: `Copy${facility ? ` '${facility.name}'` : ''}`,
      subtitle: author?.name ? `Created by ${author.name}` : undefined,
      icon: 'duplicate' as const,
      onAction: () => navigate(resolvePath(`duplicate-facility/${facilityId}`, pathname)),
      condition: !isFacilityAuthor && (!isTemplate || permissions.has('Edit Template')) && isValidFacility,
    },
    {
      title: 'Save as a template...',
      icon: 'interiorWall' as const,
      onAction: () => navigate(resolvePath(`new-template/${facilityId}`, pathname)),
      condition: !isTemplate && permissions.has('Create Template') && isValidFacility,
    },
    {
      title: 'Import/export facility data...',
      icon: 'export' as const,
      onAction: () => navigate(resolvePath(routes.modals.importExportData.replace('/', ''), pathname)),
      condition: isFacilityAuthor && permissions.has('Import & Export Facility Data'),
    },
    {
      title: 'Send to SalesHub',
      icon: 'cloudUpload' as const,
      onAction: () => navigate(resolvePath(routes.modals.sendToSalesHub.replace('/', ''), pathname)),
      condition: isFacilityAuthor,
    },
    {
      title: 'Reassign Facility',
      icon: 'cloudUpload' as const,
      onAction: () => navigate(resolvePath(`${facilityId}/user-search`, pathname)),
      condition: isFacilityAuthor && permissions.has('Reassign Facility'),
    },
  ].filter(({ condition }) => condition)

  return (
    <div className="flex items-center h-full mr-auto">
      <MenuDropDownButton isDisabled={isCustomer} label={facilityName} icon="building">
        {facilityMenuOptions.map(({ title, icon, subtitle, onAction }) => (
          <Fragment key={title}>
            <MenuItem key={title} variant="square" onAction={onAction} className="rounded-none">
              <Icon className="mr-2" name={icon} size="14" />
              <Text slot="label">{title}</Text>
              {subtitle ? <Text slot="description">{subtitle}</Text> : null}
            </MenuItem>
            <Separator />
          </Fragment>
        ))}
        {permissions.has('Create Facility') && (
          <MenuItem
            key="create-new-facility"
            variant="square"
            className="bg-baf-yellow rounded-none"
            onAction={() => navigate(resolvePath(routes.modals.newFacility.replace('/', ''), pathname))}
          >
            <Text slot="label">Create New Facility</Text>
          </MenuItem>
        )}
      </MenuDropDownButton>
      {(isCustomer && floors.length > 1) || !isCustomer ? (
        <MenuDropDownButton label={floorName} icon="floor">
          {floors
            .map((floor, _, { length }) =>
              floor ? (
                <Fragment key={floor.id}>
                  <MenuSection className="flex">
                    <MenuItem
                      className="p-4 rounded-none flex-1 text-xs"
                      onAction={() => navigate(`/facility/${facilityId}/area/${floor.id}/version/${floor.version?.id}`)}
                    >
                      <Text slot="label">{floor.name}</Text>
                    </MenuItem>
                    <MenuItem
                      className="p-2 rounded-none"
                      onAction={() => navigate(resolvePath(`edit-area/${floor.id}`, pathname))}
                    >
                      <Icon name="edit" size="14" />
                    </MenuItem>
                    <MenuItem
                      className="p-2 rounded-none"
                      onAction={() => navigate(resolvePath(`duplicate-area/${floor.id}`, pathname))}
                    >
                      <Icon name="duplicate" size="14" />
                    </MenuItem>
                    {length > 1 && (
                      <MenuItem
                        className="p-2 rounded-none"
                        onAction={() => {
                          if (confirm('Are you sure you want to delete this area?')) {
                            deleteFloor({ variables: { id: floor.id } }).then(res => {
                              const floorId = res.data?.deleteFloor?.facility?.floor?.id
                              const versionId = res.data?.deleteFloor?.facility?.floor?.version?.id
                              removeStorageItem({ facility: facilityId, area: res?.data?.deleteFloor?.id })
                              dispatch(showAlert({ type: 'success', text: 'Area deleted' }))
                              navigate(`/facility/${facilityId}/area/${floorId}/version/${versionId}`)
                            })
                          }
                        }}
                      >
                        <Icon name="trash" size="14" />
                      </MenuItem>
                    )}
                  </MenuSection>
                </Fragment>
              ) : null
            )
            .filter((floor): floor is JSX.Element => !!floor)}
          <MenuItem
            key="add-area"
            variant="square"
            className="bg-baf-yellow rounded-none"
            onAction={() => navigate(resolvePath(routes.modals.newFloor.replace('/', ''), pathname))}
          >
            <Text slot="label">Create New Area</Text>
          </MenuItem>
        </MenuDropDownButton>
      ) : null}
      {(isCustomer && versions.length > 1) || !isCustomer ? (
        <MenuDropDownButton label={versionName} icon="disk">
          {versions
            .map((version, _, { length }) =>
              version ? (
                <Fragment key={version.id}>
                  <MenuSection className="flex">
                    <MenuItem
                      className="p-4 rounded-none flex-1 text-xs"
                      onAction={() =>
                        updateFloor({
                          variables: { id: floorId ?? '', currentVersionId: version.id },
                        }).then(() => {
                          navigate(`/facility/${facilityId}/floor/${facility?.floor?.id}/version/${version.id}`)
                        })
                      }
                    >
                      <Text slot="label">{version.name}</Text>
                    </MenuItem>
                    <MenuItem
                      className="p-2 rounded-none"
                      onAction={() => navigate(resolvePath(`edit-version/${version.id}`, pathname))}
                    >
                      <Icon name="edit" size="14" />
                    </MenuItem>
                    {length > 1 && (
                      <MenuItem
                        className="p-2 rounded-none"
                        onAction={() => {
                          if (confirm('Are you sure you want to delete this version?')) {
                            deleteVersion({ variables: { id: version.id } }).then(() => {
                              dispatch(showAlert({ type: 'success', text: 'Version deleted!' }))
                              navigate(`/facility/${facilityId}`)
                            })
                          }
                        }}
                      >
                        <Icon name="trash" size="14" />
                      </MenuItem>
                    )}
                  </MenuSection>
                </Fragment>
              ) : null
            )
            .filter((floor): floor is JSX.Element => !!floor)}
          <MenuItem
            key="add-area"
            variant="square"
            className="bg-baf-yellow rounded-none"
            onAction={() => navigate(resolvePath(routes.modals.newVersion.replace('/', ''), pathname))}
          >
            <Text slot="label">Create New Version</Text>
          </MenuItem>
        </MenuDropDownButton>
      ) : null}
    </div>
  )
}

export default TopLeft
